import { storeToRefs } from 'pinia'
import type { RouterName } from '@/enums/router-name'
import {
  routerSettingSchema,
  type RouterSetting
} from '@/models/router-setting'
import { useRouterStorageStore } from '@/stores/use-router-storage-store'
import { isType } from '~library/utils/functions'

export type RouterTo<T extends RouterName> = {
  name: T
} & (T extends keyof RouterSetting ? RouterSetting[T] : object)

type Params<T extends RouterName> = T extends keyof RouterSetting
  ? RouterSetting[T] extends { params: unknown }
    ? RouterSetting[T]['params']
    : unknown
  : unknown

type Query<T extends RouterName> = T extends keyof RouterSetting
  ? RouterSetting[T] extends { query: unknown }
    ? RouterSetting[T]['query']
    : unknown
  : unknown

export function useRouterUtils<T extends RouterName>() {
  const router = useRouter()
  const route = useRoute()

  const { cacheRouterData } = useRouterStorageStore()
  const { cacheRouter, storageRouterData } = storeToRefs(
    useRouterStorageStore()
  )

  const routerMeta = computed(() => route.meta)

  const routerName = computed((): RouterName => route.name as RouterName)

  const params = computed(() => {
    if (
      isType(routerName.value, routerSettingSchema.keyof()) &&
      cacheRouter.value.includes(routerName.value)
    ) {
      return (
        storageRouterData.value?.[routerName.value] as { params?: unknown }
      )?.params as Params<T>
    }

    return route.params as Params<T>
  })

  const query = computed(() => {
    if (
      isType(routerName.value, routerSettingSchema.keyof()) &&
      cacheRouter.value.includes(routerName.value)
    ) {
      return (
        storageRouterData.value?.[routerName.value] as { query?: unknown }
      )?.query as Query<T>
    }

    return route.query as Query<T>
  })

  const back = () => router.back()

  const push = <T extends RouterName>(to: RouterTo<T>) => {
    return router.push(cacheRouterData(to))
  }

  const newTab = <T extends RouterName>(to: RouterTo<T>) => {
    return window.open(router.resolve(cacheRouterData(to)).href, '_blank')
  }

  const replace = <T extends RouterName>(to: RouterTo<T>) => {
    return router.replace(cacheRouterData(to))
  }

  return { back, push, newTab, replace, routerMeta, routerName, query, params }
}
